import {createContext, MutableRefObject, ReactNode, useContext, useEffect, useRef, useState} from "react";
import {useServerId} from "./useServerId";
import * as signalR from "@microsoft/signalr";
import {publishEvent, useEvent} from "../../hooks/useEvent";

const Context = createContext<SocketContextData>(null!);

export default function SocketContext(props: {children: ReactNode}) {

    const serverId = useServerId()

    const connection = useRef<signalR.HubConnection>()
    const [connected, setConnected] = useState<boolean>(false)

    useEffect(() => {
        if (!serverId){
            return
        }

        const conn = connection.current = new signalR.HubConnectionBuilder()
            .withUrl(`/api/client/lighthouse?serverId=${serverId}`)
            .withAutomaticReconnect()
            .build();

        conn.onclose(() => {
            publishEvent("socketClose")
        })

        conn.onreconnecting(() => {
            publishEvent("socketReconnecting")
        })

        conn.onreconnected(() => {
            publishEvent("socketReconnected")
        })

        conn.start().then(() => {
            publishEvent("socketOpen")
        }).catch(err => console.error("Failed to establish SignalR connection", err));

        return () => {
            conn.stop().catch(err => console.error("Failed to close SignalR connection", err));
        }
    }, [serverId]);

    useEvent("socketOpen", () => {
        setConnected(true)
        console.warn("Socket connected")
    })

    useEvent("socketReconnecting", () => {
        setConnected(false)
        console.warn("Socket reconnecting")
    })

    useEvent("socketReconnected", () => {
        setConnected(true)
        console.warn("Socket reconnected")
    })

    useEvent("socketClose", () => {
        setConnected(false)
        console.warn("Socket disconnected")
    })

    const context: SocketContextData = {
        connection,
        isConnected: connected
    }

    return (
        <Context.Provider value={context}>
            {props.children}
        </Context.Provider>
    )
}

export const useSocket = () => useContext(Context)

export const useRPC = (socketContext: SocketContextData, method: string, callback: (...args: any[]) => void) => {
    useEffect(() => {
        const conn = socketContext.connection.current;
        if (!socketContext.isConnected || !conn) {
            return
        }

        conn.on(method, callback);
        console.log("Registered RPC method", method);

        return () => {
            conn?.off(method);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [socketContext.isConnected]);
}

export interface SocketContextData{
    connection:  MutableRefObject<signalR.HubConnection | undefined>
    isConnected: boolean
}